import useMountEffect from '@restart/hooks/useMountEffect';
import useUpdateEffect from '@restart/hooks/useUpdateEffect';
import classNames from 'classnames/bind';
import React, { useState, useRef } from 'react';
import { RiDragMove2Line } from 'react-icons/ri';
import { Joystick } from 'react-joystick-component';
import { v4 as uuidv4 } from 'uuid';

import styles from './index.module.scss';

import BinarySwitch from '@/components/ui/BinarySwitch';
import useFlightControlCommand from '@/hooks/command/dji/useFlightControlCommand';
import { executeDJICommand } from '@/utils/CommandUtils';

const cx = classNames.bind(styles);

// DJI Stick Control 값 범위
const STICK_NEUTRAL_VALUE = 1024; // 중립 위치 (0~2048 범위의 중심)
const STICK_MAX_OFFSET = 660; // 최대 편차 (±660)

const Panel = ({ data: [robot] }) => {
  const [isVirtualJoystick, setIsVirtualJoystick] = useState(true);
  const [joystickLeftPosition, setJoystickLeftPosition] = useState({ x: 0, y: 0 });
  const [joystickRightPosition, setJoystickRightPosition] = useState({ x: 0, y: 0 });
  const stickState = useRef({
    roll: STICK_NEUTRAL_VALUE,
    pitch: STICK_NEUTRAL_VALUE,
    throttle: STICK_NEUTRAL_VALUE,
    yaw: STICK_NEUTRAL_VALUE,
  });

  const flightControlCommand = useFlightControlCommand();

  useMountEffect(() => {
    // DRC 모드 진입
    executeDJICommand(robot, flightControlCommand.drcModeEnter());

    // Heartbeat (DRC 모드 유지)
    const heartbeatInterval = setInterval(() => {
      flightControlCommand.heartbeat(robot);
    }, 10000);

    // Stick Control
    const stickControlInterval = setInterval(() => {
      const { roll, pitch, throttle, yaw } = stickState.current;
      flightControlCommand.stickControl(robot, roll, pitch, throttle, yaw);
    }, 100);

    return () => {
      clearInterval(heartbeatInterval);
      clearInterval(stickControlInterval);

      // DRC 모드 종료
      executeDJICommand(robot, flightControlCommand.drcModeExit());
    };
  });

  useUpdateEffect(() => {
    if (isVirtualJoystick) return;

    let frameId;
    const handleJoystick = () => {
      const joystick = navigator.getGamepads()[0];
      if (joystick) {
        moveJoystickLeft({
          x: joystick.axes[0],
          y: joystick.axes[1],
        });
        moveJoystickRight({
          x: joystick.axes[2],
          y: joystick.axes[3],
        });
      }

      frameId = window.requestAnimationFrame(handleJoystick);
    };

    handleJoystick();

    return () => {
      window.cancelAnimationFrame(frameId);
      stopJoystickLeft();
      stopJoystickRight();
    };
  }, [isVirtualJoystick]);

  const selectSwitch = (e) => {
    setIsVirtualJoystick(e.currentTarget.dataset.value === 'true');
  };

  const moveJoystickLeft = ({ x, y }) => {
    stickState.current.yaw = Math.trunc(STICK_NEUTRAL_VALUE + x * STICK_MAX_OFFSET);
    stickState.current.throttle = Math.trunc(STICK_NEUTRAL_VALUE + y * STICK_MAX_OFFSET);

    setJoystickLeftPosition({ x, y });
  };

  const stopJoystickLeft = () => {
    stickState.current.yaw = STICK_NEUTRAL_VALUE;
    stickState.current.throttle = STICK_NEUTRAL_VALUE;

    setJoystickLeftPosition({ x: 0, y: 0 });
  };

  const moveJoystickRight = ({ x, y }) => {
    stickState.current.roll = Math.trunc(STICK_NEUTRAL_VALUE + x * STICK_MAX_OFFSET);
    stickState.current.pitch = Math.trunc(STICK_NEUTRAL_VALUE + y * STICK_MAX_OFFSET);

    setJoystickRightPosition({ x, y });
  };

  const stopJoystickRight = () => {
    stickState.current.roll = STICK_NEUTRAL_VALUE;
    stickState.current.pitch = STICK_NEUTRAL_VALUE;

    setJoystickRightPosition({ x: 0, y: 0 });
  };

  return (
    <div className={cx('container')}>
      <BinarySwitch
        items={[
          { title: 'Virtual', value: true, onClick: selectSwitch },
          { title: 'Real', value: false, onClick: selectSwitch },
        ]}
      />
      <div className={cx('joysticks')}>
        <div className={cx(['joystick', { inactive: !isVirtualJoystick }])}>
          <Joystick
            size={120}
            stickSize={64}
            sticky={false}
            stickImage={require('./joystick-blue.png')}
            baseImage={require('./joystick-base.png')}
            move={moveJoystickLeft}
            stop={stopJoystickLeft}
            pos={joystickLeftPosition}
            disabled={!isVirtualJoystick}
          />
          <div className={cx('info')}>
            <div className={cx('item')}>{joystickLeftPosition.x.toFixed(2)}</div>
            <div className={cx('item')}>{joystickLeftPosition.y.toFixed(2)}</div>
          </div>
        </div>
        <div className={cx(['joystick', { inactive: !isVirtualJoystick }])}>
          <Joystick
            size={120}
            stickSize={64}
            sticky={false}
            stickImage={require('./joystick-blue.png')}
            baseImage={require('./joystick-base.png')}
            move={moveJoystickRight}
            stop={stopJoystickRight}
            pos={joystickRightPosition}
            disabled={!isVirtualJoystick}
          />
          <div className={cx('info')}>
            <div className={cx('item')}>{joystickRightPosition.x.toFixed(2)}</div>
            <div className={cx('item')}>{joystickRightPosition.y.toFixed(2)}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Command = {};
Command.id = uuidv4();
Command.name = 'Stick Control';
Command.displayMode = 'overlay';
Command.singleRobotOnly = true;
Command.commanderOnly = true;
Command.Icon = RiDragMove2Line;
Command.Panel = Panel;

export default Command;
